import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Button, Tabs, Tab, Table, Form, Spinner, Badge } from 'react-bootstrap';
import { FaPlus, FaSave, FaUpload, FaSync, FaArrowLeft } from 'react-icons/fa';
import api from '../../../api/apiClient';

const initialFormState = {
  version: '',
  title: '',
  content: ''
};

const PrivacyEditor = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('current');
  const [formState, setFormState] = useState(initialFormState);
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishingId, setPublishingId] = useState(null);

  const resetForm = useCallback(() => {
    setFormState(initialFormState);
    setEditingDraftId(null);
  }, []);

  const normalizeString = useCallback((value) => {
    if (!value || typeof value !== 'string') return '';
    return value.trim();
  }, []);

  const fetchVersions = useCallback(async ({ showLoader = true } = {}) => {
    try {
      if (showLoader) setLoading(true);

      const response = await api.privacy.listVersions();
      if (!response.data?.success) {
        toast.error(response.data?.message || 'Failed to load Privacy Policy versions.');
        return;
      }

      const data = response.data.data || {};
      setCurrentVersion(data.currentVersion || null);
      setDrafts(Array.isArray(data.drafts) ? data.drafts : []);
      setHistory(Array.isArray(data.history) ? data.history : []);
    } catch (error) {
      const fallbackMessage = error.response?.data?.message || error.message;
      toast.error(`Failed to load Privacy Policy: ${fallbackMessage}`);
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  const handleRefresh = useCallback(async () => {
    if (loading || refreshing) return;
    setRefreshing(true);
    try {
      await fetchVersions({ showLoader: false });
    } finally {
      setRefreshing(false);
    }
  }, [fetchVersions, loading, refreshing]);

  const handleNewDraft = useCallback(() => {
    resetForm();
    setActiveTab('drafts');
  }, [resetForm]);

  const handleEditDraft = useCallback((draft) => {
    if (!draft) return;
    setEditingDraftId(draft.id);
    setFormState({
      version: draft.version || '',
      title: draft.title || '',
      content: draft.content || ''
    });
    setActiveTab('drafts');
  }, []);

  const handleFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormState((previous) => ({
      ...previous,
      [name]: value
    }));
  }, []);

  const validateForm = useCallback(() => {
    const errors = [];
    if (!normalizeString(formState.version)) errors.push('Version is required.');
    if (!normalizeString(formState.title)) errors.push('Title is required.');
    if (!normalizeString(formState.content)) errors.push('Content cannot be empty.');

    if (errors.length) {
      errors.forEach((msg) => toast.error(msg));
      return false;
    }
    return true;
  }, [formState.content, formState.title, formState.version, normalizeString]);

  const handleSaveDraft = useCallback(async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    const payload = {
      version: normalizeString(formState.version),
      title: normalizeString(formState.title),
      content: formState.content,
      status: 'draft'
    };

    setSaving(true);
    try {
      if (editingDraftId) {
        await api.privacy.updateVersion(editingDraftId, payload);
        toast.success('Draft updated successfully.');
      } else {
        await api.privacy.createVersion(payload);
        toast.success('Draft created successfully.');
      }

      await fetchVersions();
      resetForm();
    } catch (error) {
      const fallbackMessage = error.response?.data?.message || error.message;
      toast.error(`Failed to save draft: ${fallbackMessage}`);
    } finally {
      setSaving(false);
    }
  }, [editingDraftId, fetchVersions, formState.content, formState.title, formState.version, normalizeString, resetForm, validateForm]);

  const handlePublish = useCallback(async (versionId) => {
    if (!versionId) return;
    setPublishingId(versionId);
    try {
      await api.privacy.publishVersion(versionId);
      toast.success('Privacy Policy version published successfully.');
      await fetchVersions();
      setActiveTab('current');
    } catch (error) {
      const fallbackMessage = error.response?.data?.message || error.message;
      toast.error(`Failed to publish version: ${fallbackMessage}`);
    } finally {
      setPublishingId(null);
    }
  }, [fetchVersions]);

  const sortedDrafts = useMemo(() => {
    return [...drafts].sort((a, b) => {
      const aDate = new Date(a.createdAt || 0).getTime();
      const bDate = new Date(b.createdAt || 0).getTime();
      return bDate - aDate;
    });
  }, [drafts]);

  const sortedHistory = useMemo(() => {
    return [...history].sort((a, b) => {
      const aDate = new Date(a.published_at || a.createdAt || 0).getTime();
      const bDate = new Date(b.published_at || b.createdAt || 0).getTime();
      return bDate - aDate;
    });
  }, [history]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 sm:p-6 lg:p-8">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mb-3">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/site-management')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg shadow-md hover:bg-purple-50 hover:shadow-lg transition-all duration-200 text-sm font-medium"
          >
            <FaArrowLeft className="w-4 h-4" /> Back
          </button>
          <div>
            <h1 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">Privacy Policy</h1>
            <p className="text-gray-600 mt-2">Manage drafts, publish versions, and track history of your Privacy Policy.</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            bsPrefix="custom-hamburger"
            onClick={handleRefresh}
            disabled={loading || refreshing}
            className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 px-8 py-2"
          >
            {refreshing ? (
              <>
                <Spinner animation="border" size="sm" />
                Refreshing...
              </>
            ) : (
              <>
                <FaSync className="w-4 h-4" />
                Refresh
              </>
            )}
          </Button>

          <Button
            bsPrefix="custom-hamburger"
            onClick={handleNewDraft}
            className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 px-8 py-2"
          >
            <FaPlus className="w-4 h-4" /> New Draft
          </Button>
        </div>
      </div>

      <div className="bg-white/90 rounded-3xl shadow-2xl p-6">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="text-center">
              <svg className="animate-spin h-12 w-12 text-purple-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a12 12 0 00-12 12h4z"></path></svg>
              <p className="text-lg text-gray-600">Loading Privacy Policy...</p>
            </div>
          </div>
        ) : (
          <div>
            {/* Tabs */}
            <div className="flex flex-wrap gap-3 mb-6">
              <button onClick={() => setActiveTab('current')} className={`px-4 py-2 rounded-2xl ${activeTab === 'current' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700'}`}>Current Version</button>
              <button onClick={() => setActiveTab('drafts')} className={`px-4 py-2 rounded-2xl ${activeTab === 'drafts' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700'}`}>Drafts</button>
              <button onClick={() => setActiveTab('history')} className={`px-4 py-2 rounded-2xl ${activeTab === 'history' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700'}`}>Version History</button>
            </div>

            {activeTab === 'current' && (
              <div>
                {currentVersion ? (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{currentVersion.title || 'Untitled Privacy Policy'}</h2>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-full text-sm">Version {currentVersion.version || '-'}</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-bold ${currentVersion.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'}`}>{currentVersion.status || 'Active'}</span>
                      </div>
                      <p className="text-gray-600 mt-2">Published on <span className="font-semibold">{currentVersion.published_at ? new Date(currentVersion.published_at).toLocaleString() : 'N/A'}</span></p>
                    </div>
                    <div className="bg-gray-50 border-2 border-dashed border-purple-200 rounded-3xl p-6 max-h-96 overflow-y-auto whitespace-pre-wrap text-gray-700">
                      {currentVersion.content || 'No content available for this version.'}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4 text-gray-300">Document</div>
                    <p className="text-xl text-gray-600 font-medium">No published Privacy Policy yet.</p>
                    <p className="text-gray-500 mt-2">Create a draft and publish it to make it the active version.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'drafts' && (
              <div className="grid lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold mb-4">Draft Versions</h3>
                  {sortedDrafts.length === 0 ? (
                    <div className="bg-gray-50 border-2 border-dashed border-purple-200 rounded-3xl p-8 text-center">
                      <p className="text-gray-600">No drafts found.</p>
                      <p className="text-sm text-gray-500 mt-2">Click "New Draft" to create one!</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {sortedDrafts.map((draft) => (
                        <div key={draft.id} className="bg-white border border-purple-100 rounded-2xl p-4 hover:shadow-lg transition">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-bold text-gray-900">Version {draft.version || 'Untitled'}</h4>
                              <p className="text-sm text-gray-600 mt-1">Created {draft.createdAt ? new Date(draft.createdAt).toLocaleDateString() : 'N/A'}</p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                bsPrefix="custom-hamburger"
                                onClick={() => handleEditDraft(draft)}
                                className="border-purple-300 text-purple-600 hover:bg-purple-100 rounded-lg px-3 py-2 font-semibold"
                              >
                                Edit
                              </Button>
                              <Button
                                bsPrefix="custom-hamburger"
                                onClick={() => handlePublish(draft.id)}
                                disabled={publishingId === draft.id}
                                className="border-emerald-500 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg px-3 py-2 font-semibold"
                              >
                                {publishingId === draft.id ? (
                                  <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Publishing...
                                  </>
                                ) : (
                                  'Publish'
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="text-xl font-bold mb-4">{editingDraftId ? 'Edit Draft' : 'Create New Draft'}</h3>
                  <form onSubmit={handleSaveDraft} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Version</label>
                        <input type="text" name="version" value={formState.version} onChange={handleFormChange} placeholder="e.g. 1.0" disabled={saving} className="w-full px-4 py-2 rounded-xl border border-purple-200 focus:ring-2 focus:ring-purple-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Title</label>
                        <input type="text" name="title" value={formState.title} onChange={handleFormChange} placeholder="Privacy Policy title" disabled={saving} className="w-full px-4 py-2 rounded-xl border border-purple-200 focus:ring-2 focus:ring-purple-500" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Content (English)</label>
                      <textarea rows={12} name="content" value={formState.content} onChange={handleFormChange} placeholder="Write the Privacy Policy content here..." disabled={saving} className="w-full px-4 py-3 rounded-2xl border border-purple-200 focus:ring-2 focus:ring-purple-500 resize-none" />
                      <p className="text-sm text-gray-500 mt-2">This is a plain text editor for now. We can upgrade this to a rich text editor (TipTap) in the next step.</p>
                    </div>

                    <div className="flex justify-end">
                      <Button
                        bsPrefix="custom-hamburger"
                        type="submit"
                        disabled={saving}
                        className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm px-6 py-2"
                      >
                        {saving ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Saving...
                          </>
                        ) : (
                          'Save Draft'
                        )}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {activeTab === 'history' && (
              <div>
                {sortedHistory.length === 0 ? (
                  <div className="text-center py-8 text-gray-600">No published history yet.</div>
                ) : (
                  <div className="overflow-x-auto rounded-2xl border border-purple-100 bg-white">
                    <table className="m-0 min-w-max text-xs sm:text-sm table table-bordered table-hover">
                      <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                        <tr>
                          <th className="px-6 py-3 text-left">Version</th>
                          <th className="px-6 py-3 text-left">Title</th>
                          <th className="px-6 py-3 text-left">Published At</th>
                          <th className="px-6 py-3 text-left">Status</th>
                          <th className="px-6 py-3 text-left">Notes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-purple-100">
                        {sortedHistory.map((item) => (
                          <tr key={item.id} className="hover:bg-purple-50/50">
                            <td className="px-6 py-4 font-bold text-purple-700">{item.version || '-'}</td>
                            <td className="px-6 py-4">{item.title || '-'}</td>
                            <td className="px-6 py-4 text-gray-600">{item.published_at ? new Date(item.published_at).toLocaleString() : 'N/A'}</td>
                            <td className="px-6 py-4">
                              <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${item.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>{item.status || 'Archived'}</span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">{item.changelog || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PrivacyEditor;
